<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3"
import { SbList } from "@unio/saltbox-ui"
import { computed, ref } from "vue"

import DeleteConfirmModal from "@/components/DeleteConfirmModal.vue"
import DetailRow from "@/components/DetailRow.vue"
import LabeledSection from "@/components/LabeledSection.vue"
import ListItemRow from "@/components/ListItemRow.vue"
import LoanListItem from "@/components/LoanListItem.vue"
import NarrowPage from "@/components/NarrowPage.vue"
import PersonFormSlideover from "@/components/PersonFormSlideover.vue"
import ResourceShowHeader from "@/components/ResourceShowHeader.vue"
import { useNavbar } from "@/composables/useNavbar"
import { pluralize } from "@/lib/format"
import { ICON } from "@/lib/icons"
import { personPath } from "@/routes"
import type { ItemSummary, LoanSummary, PersonSummary } from "@/types/models"

interface Props {
  person: PersonSummary
  items: ItemSummary[]
  active_loans: LoanSummary[]
  loan_history: LoanSummary[]
}

const props = defineProps<Props>()

const { title } = useNavbar({ title: props.person.name })

const editOpen = ref(false)
const deleteOpen = ref(false)
const deleting = ref(false)

const itemCount = computed(() => pluralize(props.items.length, "item"))

function confirmDelete() {
  deleting.value = true
  router.delete(personPath({ id: props.person.id }), {
    onSuccess: () => (deleteOpen.value = false),
    onFinish: () => (deleting.value = false),
  })
}
</script>

<template>
  <Head :title="title" />

  <NarrowPage>
    <ResourceShowHeader
      :icon="ICON.people"
      :title="person.name"
      :subtitle="itemCount"
      @edit="editOpen = true"
      @delete="deleteOpen = true"
    />

    <!-- Properties -->
    <SbList v-if="person.relationship || person.notes" bordered separator>
      <DetailRow v-if="person.relationship" label="Relationship" :value="person.relationship" />
      <DetailRow v-if="person.notes" label="Notes" :value="person.notes" />
    </SbList>

    <!-- Active Loans -->
    <LabeledSection v-if="active_loans.length" title="Active Loans">
      <LoanListItem v-for="loan in active_loans" :key="loan.id" :loan="loan" show-direction />
    </LabeledSection>

    <!-- Loan History -->
    <LabeledSection v-if="loan_history.length" title="Loan History" :count="loan_history.length">
      <LoanListItem v-for="loan in loan_history" :key="loan.id" :loan="loan" show-direction />
    </LabeledSection>

    <!-- Items owned by this person -->
    <LabeledSection v-if="items.length" title="Items">
      <ListItemRow v-for="item in items" :key="item.id" :item="item" variant="compact" />
    </LabeledSection>

    <UEmpty
      v-if="!items.length && !active_loans.length && !loan_history.length"
      :icon="ICON.people"
      title="No items or loans"
      description="Items assigned to this person and active loans will appear here."
    />
  </NarrowPage>

  <PersonFormSlideover v-model:open="editOpen" :person="person" />

  <DeleteConfirmModal
    v-model:open="deleteOpen"
    :title="`Delete ${person.name}?`"
    :loading="deleting"
    @confirm="confirmDelete"
  >
    <p class="text-muted text-sm">
      This will permanently delete <strong>{{ person.name }}</strong
      >.
      <template v-if="items.length">
        Their {{ pluralize(items.length, "item") }} will no longer be assigned to anyone.
      </template>
      <template v-if="active_loans.length">
        Their {{ pluralize(active_loans.length, "active loan") }} will also be deleted.
      </template>
      This action cannot be undone.
    </p>
  </DeleteConfirmModal>
</template>
